import React, { createContext, useContext, useRef } from 'react';
import { UseMutateAsyncFunction, useMutation } from 'react-query';

import { Config } from '@nw/shared/config';

import {
  RefreshAuthTokensRequestType,
  RefreshAuthTokensResponseType
} from '../services/FormsAuthServices/APIService/types';
import {
  clearAllFormsDomainTokens,
  getFormsAccessToken,
  getFormsRefreshToken,
  setFormsAccessTokenToDomain,
  setFormsRefreshTokenToDomain
} from '../utils/FormsStorageUtils';
import { clearUserSessionAndReload } from '../utils/UserSessionUtils';

import { useGetFormsAuthClientAuthDetails } from './FormsAuth/api';
import { useFormsAuthRefreshTokenAPIService } from './FormsAuth/useFormsAuthRefreshTokenAPIService';
import { useGetClientAuthCodeAPI } from './IbUserAccounts/api';

interface FormsRefreshAuthTokensProviderProps {
  children: React.ReactNode;
}

interface FormsRefreshAuthTokensContextTypes {
  refreshTokenApi: UseMutateAsyncFunction<
    RefreshAuthTokensResponseType,
    Error,
    RefreshAuthTokensRequestType
  >;
  getFormsAccessToken: () => string;
  getFormsRefreshToken: () => string;
}

export const FormsRefreshAuthTokensContext =
  createContext<FormsRefreshAuthTokensContextTypes | null>(null);

export const FormsRefreshTokenProvider = (
  props: FormsRefreshAuthTokensProviderProps
) => {
  const refreshTokenPromise = useRef<Promise<RefreshAuthTokensResponseType>>();
  const { formsAuthRefreshTokenAPIService } =
    useFormsAuthRefreshTokenAPIService();
  const { getClientAuthCodeAPI } = useGetClientAuthCodeAPI();
  const { getFormsAuthClientAuthDetailsAPI } =
    useGetFormsAuthClientAuthDetails();

  const { mutateAsync: refreshAuthTokensApi } = useMutation(
    async (requestObject: RefreshAuthTokensRequestType) => {
      if (refreshTokenPromise.current) {
        return refreshTokenPromise.current;
      }

      refreshTokenPromise.current = new Promise<RefreshAuthTokensResponseType>(
        async (resolve, reject) => {
          try {
            if (requestObject.refresh_token) {
              const data =
                await formsAuthRefreshTokenAPIService.getRefreshToken(
                  requestObject
                );

              clearAllFormsDomainTokens();
              setFormsAccessTokenToDomain(data.access_token);
              setFormsRefreshTokenToDomain(data.refresh_token);

              resolve(data);
            } else {
              throw new Error(
                JSON.stringify({
                  response: 'Invalid refresh token',
                  http_status_code: 400,
                  res_status: 'REFRESH_TOKEN_EXPIRED'
                })
              );
            }
          } catch (e) {
            const errorResponse = JSON.parse(e.message);
            if (errorResponse.res_status === 'REFRESH_TOKEN_EXPIRED') {
              try {
                const authCodeResponse = await getClientAuthCodeAPI({
                  client_id: Config['OTG_FORMS_CLIENT_ID']
                });

                const clientAuthenticationDetails =
                  await getFormsAuthClientAuthDetailsAPI({
                    client_id: Config['OTG_FORMS_CLIENT_ID'],
                    code: authCodeResponse.code
                  });

                clearAllFormsDomainTokens();
                setFormsAccessTokenToDomain(
                  clientAuthenticationDetails.access_token
                );
                setFormsRefreshTokenToDomain(
                  clientAuthenticationDetails.refresh_token
                );

                resolve(clientAuthenticationDetails);
              } catch (error) {
                clearUserSessionAndReload();
                reject(error);
              }
            } else {
              clearUserSessionAndReload();
              reject(
                new Error(
                  JSON.stringify({
                    response: 'Failed to refresh authentication token',
                    http_status_code: 400,
                    res_status: 'REFRESH_TOKEN_ERROR'
                  })
                )
              );
            }
          } finally {
            refreshTokenPromise.current = undefined;
          }
        }
      );
      return refreshTokenPromise.current;
    }
  );

  return (
    <FormsRefreshAuthTokensContext.Provider
      value={{
        refreshTokenApi: refreshAuthTokensApi,
        getFormsAccessToken,
        getFormsRefreshToken
      }}
    >
      {props.children}
    </FormsRefreshAuthTokensContext.Provider>
  );
};

const useFormsRefreshAuthTokens = (): FormsRefreshAuthTokensContextTypes => {
  const formsRefreshAuthTokensContext = useContext(
    FormsRefreshAuthTokensContext
  );

  if (!formsRefreshAuthTokensContext)
    throw new Error('Expected to be wrapped in an FormsRefreshTokenProvider');

  return formsRefreshAuthTokensContext;
};

export { useFormsRefreshAuthTokens };
